import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { FanResponseDto } from '@api'
import { useUnit } from 'effector-react'
import { usePersistentState } from 'react-persistent-state'
import { useMutation } from 'react-query'

import { useToast } from '@/components/ui/toast'
import {
  $chatsMap,
  $fanId,
  getChatsDetailsFx,
  updateFanTranslationLanguage,
} from '@/models/chattingUI/chats/chats.ts'
import {
  setAITranslatorEnabled,
  setChatterTranslationConfig,
  setFanTranslationConfigs as setEffectorFanTranslationConfigs,
} from '@/models/chattingUI/messages/translations.ts'
import { $creatorId } from '@/models/creators.ts'
import { chatsService } from '@/modules/chatting/service/chats.ts'
import type { TabInitialData } from '@/modules/chatting/state/tabs-provider.ts'
import { type ChattingTab } from '@/modules/chatting/types.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import { handleError } from '@/utils/error'

import { useMissingChats } from '../hooks/use-missing-chats'
import type { AITranslationLanguageType } from '../ui/chat-window/translation/types'

export const useComputeChatsListContext = (
  tabs: ChattingTab[],
  setTabFan: (fan: TabInitialData) => void,
) => {
  const creatorId = useUnit($creatorId)
  const { aiTranslationEnabled } = useSettingsContext()
  const fanId = useUnit($fanId)
  const chatsMap = useUnit($chatsMap)

  useMissingChats({
    tabs,
  })

  const [translationConfig, setTranslationConfig] = usePersistentState<
    Partial<{
      tutorialViewed: boolean
      ownLanguage?: AITranslationLanguageType
    }>
  >(
    { tutorialViewed: false, ownLanguage: undefined },
    { storageKey: 'ai-translation-config' },
  )

  const [fanTranslationConfigs, setFanTranslationConfigs] = usePersistentState<
    Record<
      FanResponseDto['id'],
      Partial<{
        autoTranslationEnabled: boolean
        translatorEnabled: boolean
      }>
    >
  >({}, { storageKey: `${creatorId}-translation-settings` })

  useEffect(() => {
    // workaround for effector
    setChatterTranslationConfig(translationConfig)
  }, [translationConfig])

  useEffect(() => {
    // workaround for effector
    setEffectorFanTranslationConfigs(fanTranslationConfigs)
  }, [fanTranslationConfigs])

  useEffect(() => {
    // workaround for effector
    setAITranslatorEnabled(aiTranslationEnabled)
  }, [aiTranslationEnabled])

  const [isTranslationTutorialOpen, setIsTranslationTutorialOpen] =
    useState(false)

  const updateAITranslationLanguageMutation = useMutation(
    ({
      language,
      userId,
    }: {
      language: AITranslationLanguageType | null
      userId: FanResponseDto['id']
    }) => chatsService.setTranslationLanguage(creatorId!, userId, language),
    {
      onSuccess: (_, { language, userId }) => {
        updateFanTranslationLanguage({
          fanId: userId,
          translationLanguage: language!,
        })
      },
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while updating translation language',
        )
      },
    },
  )

  const openChat = useCallback(
    async (fanId: FanResponseDto['id']) => {
      try {
        const chat =
          chatsMap[fanId] ??
          (await getChatsDetailsFx({ isMissing: false, userId: fanId }))

        const {
          id,
          name,
          fanNumber,
          avatar,
          displayName,
          username,
          avatarThumbs,
        } = chat.withUser

        setTabFan({
          fanId: id,
          fanNumber,
          avatar: avatarThumbs?.c50 ?? avatarThumbs?.c144 ?? avatar,
          title: displayName || name || username,
        })
      } catch (error) {
        handleError(error, 'Something went wrong while opening chat')
      }
    },
    [chatsMap, setTabFan],
  )

  const enableAutoTranslation = useCallback(() => {
    if (!fanId) return null

    setFanTranslationConfigs((old) => {
      const oldConfig = old[fanId]

      return {
        ...old,
        [fanId]: {
          ...oldConfig,
          autoTranslationEnabled: true,
        },
      }
    })
  }, [fanId, setFanTranslationConfigs])

  const enableTranslator = useCallback(() => {
    if (!fanId) return null

    setFanTranslationConfigs((old) => ({
      ...old,
      [fanId]: {
        ...old[fanId],
        translatorEnabled: true,
      },
    }))
  }, [fanId, setFanTranslationConfigs])

  const disableTranslator = useCallback(() => {
    if (!fanId) return null

    setFanTranslationConfigs((old) => ({
      ...old,
      [fanId]: {
        ...old[fanId],
        translatorEnabled: false,
      },
    }))
  }, [fanId, setFanTranslationConfigs])

  const disableAutoTranslation = useCallback(() => {
    if (!fanId) return null

    setFanTranslationConfigs((old) => ({
      ...old,
      [fanId]: {
        ...old[fanId],
        autoTranslationEnabled: false,
      },
    }))
  }, [fanId, setFanTranslationConfigs])

  const openTranslationTutorial = useCallback(() => {
    if (!translationConfig.tutorialViewed) {
      setIsTranslationTutorialOpen(true)
    }
  }, [translationConfig.tutorialViewed])

  const closeTranslationTutorial = useCallback(
    (checkedAsRead: boolean) => {
      if (!fanId) return null

      setIsTranslationTutorialOpen(false)
      if (checkedAsRead) {
        setTranslationConfig((old) => ({
          ...old,
          tutorialViewed: true,
        }))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fanId, setFanTranslationConfigs],
  )

  const setOwnLanguage = useCallback(
    (language: AITranslationLanguageType | undefined) => {
      setTranslationConfig((old) => ({
        ...old,
        ownLanguage: language,
      }))
    },
    [setTranslationConfig],
  )

  const setFanLanguage = useCallback(
    (language: AITranslationLanguageType | null) => {
      if (!fanId) return null

      updateAITranslationLanguageMutation.mutate({
        userId: fanId,
        language,
      })
    },
    [fanId, updateAITranslationLanguageMutation],
  )

  const values = useMemo(
    () => ({
      openChat,
      enableAutoTranslation,
      disableAutoTranslation,
      enableTranslator,
      disableTranslator,
      openTranslationTutorial,
      setOwnLanguage,
      setFanLanguage,
      closeTranslationTutorial,
      isTranslationTutorialOpen,
    }),
    [
      openChat,
      enableAutoTranslation,
      disableAutoTranslation,
      openTranslationTutorial,
      setOwnLanguage,
      enableTranslator,
      disableTranslator,
      setFanLanguage,
      closeTranslationTutorial,
      isTranslationTutorialOpen,
    ],
  )

  return values
}

type ChatsListContextValue = ReturnType<typeof useComputeChatsListContext>
export const ChatsListContext = createContext<ChatsListContextValue | null>(
  null,
)
ChatsListContext.displayName = 'ChatsListContext'

export const useChats = () => {
  const context = useContext(ChatsListContext)

  if (!context) {
    throw new Error('useChats must be used within ChatsListContextProvider')
  }

  return context
}
